import {
    createContext,
    PropsWithChildren,
    ReactElement,
    Suspense,
    use,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import Footer from "../footer";
import PageHeader from "./page-header";
import { Sidebar, SidebarTrigger } from "./sidebar";
import { Spinner } from "./spinner";
import { ScrollAreaSafe } from "./scroll-area";
import { cn } from "@/lib/utils";
import { useLocation } from "react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FeatureFlag } from "@/constants";
import { Banner } from "@/components/ui/banner";
import { BodyDefault, Link } from "@/components/ui/typography";

type SideBarWithRanking = {
    id: string;
    component: typeof Sidebar;
    // higher ranked sidebars take precetent over lower ranked (works like z-index)
    ranking: number;
};

type ISideBarContext = {
    sidebars: SideBarWithRanking[];
    addSideBar: (newSidebar: SideBarWithRanking) => void;
    removeSidebar: (sidebarId: string) => void;
};

const SidebarStackerContext = createContext<ISideBarContext>({
    sidebars: [],
    addSideBar: () => {
        console.warn("No context addSideBar handler has been set.");
    },
    removeSidebar: () => {
        console.warn("No context removeSidebar handler has been set.");
    },
});

const useSidebarStacker = () => {
    const context = use(SidebarStackerContext);
    if (!context) {
        throw new Error(
            "useSidebar must be used within an SidebarContextProvider",
        );
    }
    return context;
};

export const SidebarStackerProvider: React.FC<PropsWithChildren> = ({
    children,
}): ReactElement => {
    const [sidebars, setSidebars] = useState<SideBarWithRanking[]>([]);

    const addSideBar = useCallback(
        (newSidebar: SideBarWithRanking) =>
            setSidebars((oldSidebars) =>
                [...oldSidebars, newSidebar].toSorted((sidebarA, sidebarB) => {
                    return sidebarB.ranking - sidebarA.ranking;
                }),
            ),
        [],
    );

    const removeSidebar = useCallback(
        (oldSidebarId: string) =>
            setSidebars((oldSidebars) =>
                oldSidebars.filter(({ id }) => id !== oldSidebarId),
            ),
        [],
    );

    return (
        <SidebarStackerContext value={{ sidebars, addSideBar, removeSidebar }}>
            {children}
        </SidebarStackerContext>
    );
};

/**
 * Use this component to render out your sidebars to the top level
 */
export const SidebarStacker = ({
    id,
    ranking,
    component,
}: SideBarWithRanking): ReactElement => {
    const { addSideBar, removeSidebar } = useSidebarStacker();

    useEffect(() => {
        addSideBar({
            id,
            ranking,
            component,
        });
        return () => {
            removeSidebar(id);
        };
    }, []);

    return <></>;
};

export const SidebarLayout = ({ children }: PropsWithChildren) => {
    const { sidebars } = useSidebarStacker();

    const TopRankedSidebar = sidebars.at(0)?.component;
    return (
        <div className="z-0 grid h-full w-full grid-cols-[auto_1fr]">
            {TopRankedSidebar ? (
                <TopRankedSidebar collapsible="offcanvas">
                    <SidebarTrigger />
                </TopRankedSidebar>
            ) : (
                <div /> // Empty element required here to take up the first grid column to ensure PageLayout takes the second column
            )}
            <PageLayout>{children}</PageLayout>
        </div>
    );
};

export const PageLayout = ({
    children,
    className,
    padded = true,
    ...props
}: { padded?: boolean } & React.ComponentProps<typeof ScrollAreaSafe>) => {
    const { pathname } = useLocation();
    const scrollAreaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        scrollAreaRef.current?.scrollTo({ top: 0 });
    }, [pathname]);

    return (
        <ScrollAreaSafe
            ref={scrollAreaRef}
            containerClassName={cn(
                padded && "h-full w-full max-w-600 justify-self-center",
                className,
            )}
            {...props}
        >
            <div className="px-wider flex min-h-full w-full flex-col overflow-hidden">
                <PageHeader />
                {/* `relative` here is important, as it acts as an element to "catch" the ErrorUI, which is positioned as `absolute` */}
                <div className="relative grow">
                    <Suspense fallback={<Spinner sizing="largest" />}>
                        {children}
                    </Suspense>
                </div>
                <Footer />
            </div>
        </ScrollAreaSafe>
    );
};

export const RootLayout = ({ children }: PropsWithChildren) => {
    const flags = useFlags();

    return (
        <div className="grid h-screen w-full grid-rows-[auto_auto_minmax(0,1fr)] overflow-hidden">
            {flags[FeatureFlag.PL_719_WARNING_BANNER] ? (
                <Banner
                    appearance="warning"
                    aria-label="System migration notice"
                >
                    <BodyDefault as="p" className="text-inherit">
                        Your chargers may be affected by a system migration. If
                        so, ask your IT team to update your firewall whitelist
                        from{" "}
                        <Link
                            className="text-inherit underline hover:text-inherit active:text-inherit"
                            rel="noreferrer"
                            target="_blank"
                            to="https://hws.jetcharge.com.au"
                        >
                            hws.jetcharge.com.au
                        </Link>{" "}
                        to{" "}
                        <Link
                            className="text-inherit underline hover:text-inherit active:text-inherit"
                            rel="noreferrer"
                            target="_blank"
                            to="https://wsg.jetcharge.com.au"
                        >
                            wsg.jetcharge.com.au
                        </Link>{" "}
                        before 8 June to avoid connectivity issues.
                    </BodyDefault>
                </Banner>
            ) : (
                // div is important here as part of the grid layout, do not remove unless also
                // updating `grid-rows-[auto_auto_minmax(0,1fr)]` to `grid-rows-[auto_minmax(0,1fr)]`
                // and removing the banner.
                <div></div>
            )}
            {children}
        </div>
    );
};

/**
 * Used for the main/details column layouts you see in device group / device pages
 */
export const TwoColumnGrid = ({
    children,
    className,
    forceSingleColumn,
    ...props
}: PropsWithChildren<{ forceSingleColumn?: boolean }> &
    React.HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            className={cn(
                // The wonky 1199px breakpoint here is so that we get a chromatic snapshot (1200px) with the right view
                // *:min-w-0 stops a grid item's content (eg. a chart) forcing its track wider than
                // the fr/minmax sizing once it renders, which would otherwise push the other column out.
                // items-start stops the shorter column being stretched to match the taller one's height.
                "gap-wide grid grid-cols-1 items-start *:min-w-0",
                !forceSingleColumn &&
                    "min-[1199px]:grid-cols-[1fr_minmax(350px,24rem)]",
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
};
